import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { db, auth, functions } from '../firebase';
import { collection, onSnapshot, query, doc, getDoc, setDoc, updateDoc, where, getDocs, documentId, orderBy, limit } from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { ipfsGateway, FIREBASE_PROJECT_ID, API_BASE_URL, BASE_MAX_STAMINA, TOKEN_CONTRACT, TOKENS, tokenContracts, tokenDecimals, buildingConfigClient, ATOMIC_API_ENDPOINTS, itemTemplates, EMBLEM_NAMES, MASTER_ACCOUNT_WAX_ID, ADMIN_ACCOUNTS, STAKING_TIERS, VAULT_TIERS, VAULT_TOKEN_INFO, VAULT_TOKEN_VALUE, getUpgradeCostClient, Client, calculateDiminishedBoost, calculateMaxHp, calculateXpForNextLevel } from '../constants/gameConfig';
import characterImage from '../assets/firecat.png';
import placeholderImage from '../assets/equip.png';
function TodaysAirdrops() {
  const [airdrops, setAirdrops] = useState({ nfts: [], tokens: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAirdrops = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `${API_BASE_URL}/getTodaysAirdrops`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch rewards. Server responded with status: ${response.status}`);
        }
        const data = await response.json();
        setAirdrops(data);
      } catch (error) {
        console.error("Failed to fetch today's airdrops:", error);
        setError(error.message);
        setAirdrops({ nfts: [], tokens: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchAirdrops();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-10 w-full max-w-5xl mx-auto">
        <div className="text-zinc-500 animate-pulse font-mono">Loading Today's Rewards...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-5xl mx-auto bg-red-950/40 backdrop-blur border border-red-900/50 rounded-xl p-6 mb-6">
        <h3 className="text-xl font-bold text-red-500 text-center mb-2">Could Not Load Rewards</h3>
        <p className="text-red-300/70 text-center text-sm">{error}</p>
      </div>
    );
  }

  if (!airdrops) {
    return null;
  }

  return (
    <div className="w-full max-w-5xl mx-auto bg-black/60 backdrop-blur-md border border-zinc-800 rounded-xl p-6 mb-8 shadow-[0_0_15px_rgba(0,255,255,0.05)] text-slate-200">
      <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 text-center mb-6">
        Daily Reset & Rewards Explained
      </h3>

      {/* Eligibility Criteria */}
      <div className="border-b border-zinc-800 pb-6 mb-6">
        <h4 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
          <span className="text-cyan-400">⚡</span> How It Works
        </h4>
        <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
          At the daily reset (8:00 AM ET), rewards are processed. To be eligible for <strong className="text-white">any</strong> daily rewards, you must meet the <strong className="text-green-400">Investor Protocol</strong>:
        </p>
        <div className="flex flex-wrap gap-4">
          <div className="bg-zinc-900/80 border border-zinc-700 rounded-lg px-4 py-2 flex items-center gap-3">
            <span className="text-xs uppercase text-zinc-500">Player Level</span>
            <span className="font-mono font-bold text-orange-400">2+</span>
          </div>
          <div className="bg-zinc-900/80 border border-zinc-700 rounded-lg px-4 py-2 flex items-center gap-3">
            <span className="text-xs uppercase text-zinc-500">Daily Points</span>
            <span className="font-mono font-bold text-orange-400">2500+</span>
          </div>
          <div className="bg-zinc-900/80 border border-zinc-700 rounded-lg px-4 py-2 flex items-center gap-3">
            <span className="text-xs uppercase text-zinc-500">Vault Score</span>
            <span className="font-mono font-bold text-orange-400">250,000+</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Weekly Season Logic */}
        <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-5">
          <h4 className="text-md font-bold text-cyan-400 mb-3 flex items-center gap-2">
            1. Monthly Season Progress
          </h4>
          <p className="text-sm text-zinc-400 leading-relaxed">
            Your Daily Points are <strong className="text-white">not lost</strong>! At reset, they are added to your <strong className="text-white">Monthly Season Score</strong>.
            Accumulate points all month to win a share of the <strong className="text-orange-400">20,000,000 FIREMAN</strong> Monthly Jackpot.
          </p>
        </div>

        {/* Partner Token Airdrops */}
        <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-5">
          <h4 className="text-md font-bold text-cyan-400 mb-3 flex items-center gap-2">
            2. Partner Token Airdrops
          </h4>
          {airdrops.tokens.length > 0 ? (
            <>
              <p className="text-sm text-zinc-400 mb-4">
                The following token pools are shared proportionally among all eligible players based on Daily Points.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {airdrops.tokens.map((token, index) => (
                  <div key={`token-${index}`} className="bg-black/50 border border-zinc-700 rounded-lg p-3 flex flex-col justify-center items-center text-center">
                    <span className="font-bold text-green-400 mb-1">{token.symbol}</span>
                    <span className="text-xs text-zinc-500">from {token.partner}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex h-full items-center justify-center p-4">
              <p className="text-sm text-zinc-500 italic">No active partner token airdrops today.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TodaysAirdrops;
